/**
 * Biometric encryption utilities for secure image encryption.
 * Used by Onboard (registration) and Dashboard (biometric approval).
 */
import axios from 'axios';
import { Buffer } from 'buffer';

export function bufferToHex(buffer) {
  return [...new Uint8Array(buffer)]
    .map(b => b.toString(16).padStart(2, '0'))
    .join('');
}

export function base64ToArrayBuffer(base64) {
  const binaryString = window.atob(base64);
  const len = binaryString.length;
  const bytes = new Uint8Array(len);
  for (let i = 0; i < len; i++) {
    bytes[i] = binaryString.charCodeAt(i);
  }
  return bytes.buffer;
}

/**
 * Encrypt a base64 image using KMS data key and AES-CBC.
 * @param {string} base64Image - Base64-encoded image data (without data URL prefix)
 * @param {string} apiBaseUrl - API base URL for KMS endpoint
 * @returns {Promise<{ encryptedDataKey: string, encryptedImageData: string }>}
 */
export async function encryptBiometricImage(base64Image, apiBaseUrl) {
  const keyResponse = await axios.post(`${apiBaseUrl}/kms/generate-data-key`, {}, {
    timeout: 60000,
  });
  const { plaintext, ciphertext } = keyResponse.data;

  const plaintextKeyBuffer = base64ToArrayBuffer(plaintext);
  const key = await window.crypto.subtle.importKey(
    'raw',
    plaintextKeyBuffer,
    'AES-CBC',
    true,
    ['encrypt', 'decrypt']
  );

  const iv = window.crypto.getRandomValues(new Uint8Array(16));
  const imageBuffer = Buffer.from(base64Image, 'base64');
  const encryptedImageBuffer = await window.crypto.subtle.encrypt(
    { name: 'AES-CBC', iv },
    key,
    imageBuffer
  );
  const encryptedImageHex = bufferToHex(encryptedImageBuffer);
  const ivHex = bufferToHex(iv);
  const encryptedImageData = `${ivHex}:${encryptedImageHex}`;

  return {
    encryptedDataKey: ciphertext,
    encryptedImageData,
  };
}
